import { TokenContext } from '@/Contexts/TokenContext';
import { UsersContext } from '@/Contexts/UsersContext';
import { ApplicationDeadlineProps } from '@/models/ApplicationDeadline';
import { ProgramNames } from '@/models/Program';
import { UserType } from '@/models/User';
import { baseUrl } from '@/utils/constants';
import { useCallback, useContext } from 'react';
import { useCustomFetch } from './useCustomFetch';
import { ApplicationDeadlineAdminProps } from '@/models/Admin/AdminApplicationCycle/AdminApplicationCycle';
import { ConfigContext } from '@/Contexts/ConfigContext';
import { ApplicationCycleContext } from '@/Contexts/ApplicationCycleContext';

export function useApplicationDeadline() {
  const { authResult } = useContext(TokenContext);
  const { userType } = useContext(UsersContext);
  const { deadlines } = useContext(ConfigContext);
  const { applicationCycles } = useContext(ApplicationCycleContext);
  const { customFetch } = useCustomFetch();

  // The deadlines list spans every application cycle a state/program has ever had, so a
  // deadline must be matched to its state/program's *currently active* ApplicationCycle by
  // ApplicationCycleId rather than by picking the chronologically earliest date - otherwise a
  // long-closed cycle's stale deadline (e.g. 2023) would be picked over the current one.
  // A program can have several active cycles at once (Standard, Growth Award, state-specific
  // State Questions) and the deadline row lives on only one of them, so every matching active
  // cycle is considered. A deadline whose date has passed is still returned while its cycle is
  // active, so users can see that the deadline was missed.
  const findDeadlineForActiveCycle = useCallback(
    (stateId: string, programId: ProgramNames | null): ApplicationDeadlineProps | null => {
      const activeCycleIds = new Set(
        (applicationCycles ?? [])
          .filter(
            (cycle) =>
              cycle.active &&
              (cycle.stateId == null || cycle.stateId === stateId) &&
              (programId == null || programId === ProgramNames._ || cycle.programId === programId)
          )
          .map((cycle) => cycle.id)
      );

      if (activeCycleIds.size === 0) {
        return null;
      }

      return (
        deadlines.find(
          (d): d is NonNullable<ApplicationDeadlineProps> =>
            d != null &&
            d.stateId === stateId &&
            d.applicationCycleId != null &&
            activeCycleIds.has(d.applicationCycleId)
        ) ?? null
      );
    },
    [applicationCycles, deadlines]
  );

  const lookupApplicationStateDeadline = useCallback(
    (stateId: string, programId: ProgramNames | null): string | null => {
      const match = findDeadlineForActiveCycle(stateId, programId);
      return match?.stateReviewDeadline ?? null;
    },
    [findDeadlineForActiveCycle]
  );

  const lookupApplicationApplicantDeadline = useCallback(
    (stateId: string, programId: ProgramNames | null): string | null => {
      const match = findDeadlineForActiveCycle(stateId, programId);
      return match?.applicantDeadline ?? match?.stateReviewDeadline ?? null;
    },
    [findDeadlineForActiveCycle]
  );

  const lookupDeadline = useCallback(
    (stateId: string, programId: ProgramNames | null): string | null => {
      switch (userType) {
        case UserType.Adf:
        case UserType.RegionalCoordinator:
        case UserType.NationalProgrammeLead:
          return null;
        case UserType.StateCoordinator:
          return lookupApplicationStateDeadline(stateId, programId);
        case UserType.Applicant:
        default:
          return lookupApplicationApplicantDeadline(stateId, programId);
      }
    },
    [lookupApplicationApplicantDeadline, lookupApplicationStateDeadline, userType]
  );

  const getApplicationDeadlineDetails = useCallback(
    async (stateId: string, programId: ProgramNames): Promise<ApplicationDeadlineProps> => {
      const url = `${baseUrl}applications/deadline/${programId}/${stateId}`;
      const response = await customFetch<ApplicationDeadlineProps>(url, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
      });

      if (response == null) return null;

      return response;
    },
    [authResult, customFetch]
  );

  const getAllApplicationDeadlines = useCallback(async (): Promise<ApplicationDeadlineProps[]> => {
    const url = `${baseUrl}applicationdeadlines/getall`;
    const response = await customFetch<ApplicationDeadlineProps[]>(url, {
      method: 'GET',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        Authorization: `Bearer ${authResult.accessToken}`,
      },
    });

    if (!Array.isArray(response)) return [];

    return response.filter(
      (item): item is NonNullable<ApplicationDeadlineProps> =>
        item != null && typeof item === 'object'
    );
  }, [authResult, customFetch]);

  const getApplicationDeadlineDataGrid = useCallback(async (): Promise<
    ApplicationDeadlineAdminProps[]
  > => {
    const url = `${baseUrl}admin/ApplicationDeadlines/getAll`;

    const response = await customFetch<ApplicationDeadlineAdminProps[]>(url, {
      method: 'GET',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        Authorization: `Bearer ${authResult.accessToken}`,
      },
    });

    const allDeadlines = Array.isArray(response) ? response : [];

    return allDeadlines.filter(
      (item): item is ApplicationDeadlineAdminProps => item != null && typeof item === 'object'
    );
  }, [authResult, customFetch]);

  const updateApplicationDeadline = useCallback(
    async (deadline: ApplicationDeadlineAdminProps): Promise<ApplicationDeadlineAdminProps> => {
      const url = `${baseUrl}admin/ApplicationDeadlines/UpdateDeadlines`;

      const body = {
        ApplicationCycleId: deadline.applicationCycleId ?? deadline.applicationCycle.id,
        StateId: deadline.stateId,
        ApplicantDeadline: deadline.applicantDeadline ?? null,
        StateReviewDeadline: deadline.stateReviewDeadline ?? null,
      };

      return customFetch<ApplicationDeadlineAdminProps>(url, {
        method: 'PUT',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
        body: JSON.stringify(body),
      });
    },
    [authResult, customFetch]
  );

  return {
    getAllApplicationDeadlines,
    getApplicationDeadlineDetails,
    getApplicationDeadlineDataGrid,
    updateApplicationDeadline,
    lookupDeadline,
  };
}
